import { useAuthStore } from "@/stores/auth";
import i18n from "@/framework-originated/i18n/config";

const BASE = "/api/v1";

// 后端统一响应 envelope：{code, message, data}。成功 code=0、HTTP 200；
// 失败保留 HTTP 状态码，message 为按 Accept-Language 协商的本地化文案。
interface Envelope<T> {
  code: number;
  message: string;
  data: T;
}

export class ApiError extends Error {
  status: number;
  code?: number;
  body: unknown;
  constructor(status: number, code: number | undefined, body: unknown, message: string) {
    super(message);
    this.status = status;
    this.code = code;
    this.body = body;
  }
}

export class RequestTimeoutError extends Error {
  timeoutMs: number;
  constructor(timeoutMs: number) {
    super(`请求超过 ${Math.ceil(timeoutMs / 1000)} 秒，提交结果待确认`);
    this.name = "RequestTimeoutError";
    this.timeoutMs = timeoutMs;
  }
}

type RequestOptions = RequestInit & { timeoutMs?: number };
const DEFAULT_REQUEST_TIMEOUT_MS = 30_000;

async function request<T>(path: string, init?: RequestOptions): Promise<T> {
  const token = useAuthStore.getState().token;
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    "Accept-Language": i18n.language === "en" ? "en" : "zh",
    ...(init?.headers as Record<string, string> | undefined),
  };
  if (token) headers["Authorization"] = `Bearer ${token}`;

  const controller = new AbortController();
  const callerSignal = init?.signal;
  const timeoutMs = init?.timeoutMs ?? DEFAULT_REQUEST_TIMEOUT_MS;
  let timedOut = false;
  const abortFromCaller = () => controller.abort();
  if (callerSignal?.aborted) controller.abort();
  else callerSignal?.addEventListener("abort", abortFromCaller, { once: true });
  const timer = window.setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, timeoutMs);

  let res: Response;
  let responseText: string;
  try {
    const { timeoutMs: _timeoutMs, signal: _signal, ...fetchInit } = init ?? {};
    res = await fetch(`${BASE}${path}`, { ...fetchInit, headers, signal: controller.signal });
    responseText = await res.text();
  } catch (error) {
    if (timedOut) throw new RequestTimeoutError(timeoutMs);
    throw error;
  } finally {
    window.clearTimeout(timer);
    callerSignal?.removeEventListener("abort", abortFromCaller);
  }
  const body = responseText ? safeJson(responseText) : null;

  if (!res.ok) {
    let msg = `HTTP ${res.status}`;
    let code: number | undefined;
    if (body && typeof body === "object") {
      const b = body as {
        code?: unknown;
        message?: unknown;
        error?: unknown;
        data?: { error?: unknown } | null;
      };
      if (typeof b.code === "number") code = b.code;
      const detail = b.data && typeof b.data === "object" ? b.data.error : undefined;
      if (typeof detail === "string" && detail) msg = detail;
      else if (typeof b.message === "string" && b.message) msg = b.message;
      else if (typeof b.error === "string" && b.error) msg = b.error;
    }
    if (res.status === 401) {
      useAuthStore.getState().logout();
    }
    throw new ApiError(res.status, code, body, msg);
  }
  // 成功：解包 {code:0,message,data}；兼容尚未迁移的裸响应。
  if (body && typeof body === "object") {
    const env = body as Envelope<T>;
    if (typeof env.code === "number" && typeof env.message === "string" && "data" in env && env.code === 0) {
      return env.data;
    }
  }
  return body as T;
}

function safeJson(s: string): unknown {
  try {
    return JSON.parse(s);
  } catch {
    return s;
  }
}

export const api = {
  get: <T,>(p: string) => request<T>(p),
  post: <T,>(p: string, body?: unknown, timeoutMs?: number) =>
    request<T>(p, { method: "POST", body: body ? JSON.stringify(body) : undefined, ...(timeoutMs ? { timeoutMs } : {}) }),
  put: <T,>(p: string, body?: unknown) =>
    request<T>(p, { method: "PUT", body: body ? JSON.stringify(body) : undefined }),
  del: <T,>(p: string) => request<T>(p, { method: "DELETE" }),
};

// --- domain types ---
  // v6 重构(见 doc/010、doc/011):provider 表与
// DistributionTemplate 已删,改为 relay/push 两种站点与模板,三主键
// (site_type, adapter_type, code);协议类型由 protocol_type int 常量驱动。

export type AuthType = "token" | "basic";
export type RelayAdapterType = "newapi" | "newapi-seller" | "sub2api" | "oneapi";
// v6 I-23:推送适配器命名 custom[-<merchant>],一商家一自定义(custom-ezlinkai-com
// 是首个实接)。custom-push 是兜底 stub。TS 端不再用字面量联合(否则每加一商家
// 都要改这里),改成 string + 运行时由后端 pushadapter registry 校验。
export type PushAdapterType = string;
export type TaskStatus = "staged" | "pending" | "dispatching" | "success" | "failed" | "unknown" | "abandoned";
export type KeyStatus = "active" | "inactive" | "expired";
// doc/050 §4.1: 列表排序方向;page / page_size / sort / order 为 7 个列表页统一 query 契约。
export type SortOrder = "asc" | "desc";
export interface ListSortParams {
  sort?: string;
  order?: SortOrder;
}
export interface ListPageResult<T> {
  items: T[];
  total: number;
  page: number;
  page_size: number;
}
export type UsageAttemptStatus = "success" | "failed" | "unsupported" | "no_data";
export type UsageCoverage = "complete" | "partial" | "none";
export type UsageGranularity = "detailed" | "aggregate";
export interface ConsoleProxyResult {
  code: number;
  message: string;
  data?: unknown;
  upstream_status: number;
}

// RelaySite — /relay-sites(中转站,后端 RelaySite / siteResp)。
export interface RelaySite {
  id: number;
  // code — 站点稳定身份(表内唯一,新建必填,创建后不可改)。分发混合批用
  // (site_type, code) 复合键携带,故 relay_site.code 与 push_site.code 各自独立唯一。
  code: string;
  name: string;
  // 站点级目标代号, 供应商端可见 / 发布选目标用
  target_code: string;
  url: string;
  site_type: "relay";
  adapter_type: RelayAdapterType;
  auth_type: AuthType;
  auth_hint: string;
  platform_user_id: number;
  group: string;
  models: string | null;
  model_mapping: string | null;
  // 站点级扩展配置（非密，relay_site.site_options，与 push_site 同构）。
  // module_path = 业务模块路径，决定渠道 API 前缀 /api/{module_path}/channel；
  // 空/缺省 = /api/channel。与 url 里的 path 无关：url 只填 scheme://host。
  site_options?: Record<string, string>;
  enabled: boolean;
  last_tested_at: string | null;
  last_test_result: string;
  remark: string;
  created_at: string;
  updated_at: string;
}

// PushSite — /push-sites(推送站,后端 PushSite)。auth_type 是 basic 时站点
// 凭证同时含 user_name(账号)与 auth(密码);token 时只填 auth。
// doc/011:无站点级 api_contract —— 商户级参数统一放绑定行 field_mapping
// (platform_channel_type / channel_group_id / name / mode / priority 等)。
// user_name 是站点凭证,与 password 一起在 basic 认证下经 user_name_ciphertext
// + auth_ciphertext 列加密落库。
export interface PushSite {
  id: number;
  // code — 见 RelaySite.code;push_site 表内唯一,与 relay_site.code 相互独立。
  code: string;
  name: string;
  // 站点级目标代号, 供应商端可见 / 发布选目标用
  target_code: string;
  url: string;
  site_type: "push";
  adapter_type: PushAdapterType;
  auth_type: AuthType;
  auth_hint: string;
  // user_name_hint 是 basic 认证下的账号末四位提示(空 = token 或未配)。
  user_name_hint: string;
  user_name?: string;
  models: string | null;
  model_mapping: string | null;
  // site_options 是站点级非密扩展配置(platform_user_id/upload_mode/multi_key_mode)。
  site_options: Record<string, string>;
  distributor_id: number;
  enabled: boolean;
  remark: string;
  created_at: string;
  updated_at: string;
}

// APIKey — /api-keys(后端 keyResp)。v6 起 provider 字段改为 supplier_field +
// protocol_type(int)。
export interface APIKey {
  id: number;
  name: string;
  supplier_field: string;
  key_hint: string;
  base_url: string;
  models: string;
  unit_price: number;
  protocol_type: number | null;
  expires_at: string | null;
  status: KeyStatus;
  remark: string;
  owner_type: "platform" | "supplier";
  owner_supplier_id?: number | null;
  owner_supplier_username?: string;
  duplicate_of_id?: number | null;
  suspected_duplicate: boolean;
  relay_task_count?: number;
  push_task_count?: number;
  created_at: string;
  updated_at: string;
}

// DistributionTask — /distributions(后端 distTaskResp)。v6 起去掉
// template_id/channel_type/finished_at,改为 template_snapshot/completed_at/
// protocol_type 等。
export interface DistributionTask {
  id: number;
  key_id: number;
  operation_id?: string | null;
  site_id: number;
  site_type: string;
  // site_code — 站点稳定身份(表内唯一)。live-watch / 记录页按 (site_type, site_code)
  // 复合键解析站点名(裸 site_id 会跨 relay/push 撞号)。
  site_code: string;
  adapter_type: string;
  template_id?: number | null;
  template_snapshot: string;
  status: TaskStatus;
  remote_channel_id: number | null;
  remote_reference?: string;
  local_template_id?: number | null;
  groups: string;
  priority: number | null;
  models_snapshot?: string | null;
  model_mapping: string;
  error_message: string;
  retry_count: number;
  max_retries: number;
  dispatched_at: string | null;
  completed_at: string | null;
  update_diff: string;
  protocol_type: number | null;
  created_at: string;
  updated_at: string;
}

// DistributionExchange — 分发出站 HTTP 调用快照(已脱敏)。
// 仅 GET /distributions/:id 返回;列表接口不携带。
export interface DistributionExchange {
  attempt: number;
  seq: number;
  operation: string;
  adapter: string;
  method: string;
  url: string;
  request_headers: string;
  request_body: string;
  response_status: number;
  response_body: string;
  duration_ms: number;
  error_message: string;
  created_at: string;
}

export interface DistributionTaskDetail extends DistributionTask {
  exchanges?: DistributionExchange[];
}

// 协议类型 — /protocol-types 只读列表(type = protocol_type int,new-api ChannelType)。
export interface ProtocolType {
  name: string;
  type: number;
}

// MainTemplate — /main-templates,主键 id;引用 MainProtocol(main_protocol_id)。
// v6→main 域重构:删 site_type/adapter_type/groups/priority(adapter 由绑定站点
// 决定,groups/priority 下沉到中转站绑定 TemplateRelaySite)。protocol_type 从
// 主协议 denormalize。
export interface MainTemplate {
  id: number;
  main_protocol_id: number;
  code: string;
  name: string;
  eyebrow: string;
  description: string;
  protocol_type: number;
  models: string;
  model_mapping: string;
  enabled: boolean;
  created_at: string;
  updated_at: string;
}

// TemplateRelaySite — 中转站绑定(main_template × relay_site)。主键
// (template_id, site_id);承载 groups + priority。
export interface TemplateRelaySite {
  template_id: number;
  site_id: number;
  protocol_type: number;
  groups: string;
  priority: number;
  proxy: string;
  models: string | null;
  model_mapping: string | null;
  effective_models: string;
  effective_model_mapping: string;
  models_source: ModelConfigSource;
  model_mapping_source: ModelConfigSource;
  created_at: string;
  updated_at: string;
  site_name: string;
  template_name: string;
}

// TemplatePushSite — 推送站绑定(main_template × push_site)。主键
// (template_id, site_id);承载 push_template_id(远程模板ID)+ field_mapping(JSON)。
export interface TemplatePushSite {
  template_id: number;
  site_id: number;
  push_template_id: number;
  field_mapping: string;
  models: string | null;
  model_mapping: string | null;
  effective_models: string;
  effective_model_mapping: string;
  models_source: ModelConfigSource;
  model_mapping_source: ModelConfigSource;
  created_at: string;
  updated_at: string;
  site_name: string;
  template_name: string;
}

export type ModelConfigSource = "request" | "binding" | "site" | "template" | "default";

// --- 粘贴检测(IT-23) ---
export type ProbeStatus = "active" | "inactive" | "throttled" | "timeout" | "unknown";
export interface ProbeResult {
  key_index: number;
  key_hint: string;
  status: ProbeStatus;
  error_code: string;
  error_message: string;
  latency_ms: number;
  provider_request_id?: string;
}
export interface ProbeSettings {
  protocol_mode?: string;
  proxy_url?: string;
  timeout_seconds?: number;
  concurrency?: number;
  rate_per_minute?: number; // 20(标准)/5(保守)
  prompt?: string;
  real_call?: boolean;
}
export const healthCheckApi = {
  // 同步批量探测:明文瞬时、不落库、不改 Key 状态;Key 上限 100。
  probe: (body: {
    template_id?: number;
    models?: string[];
    keys: string[];
    settings?: ProbeSettings;
  }) =>
    request<{ results: ProbeResult[] }>("/health-check/probe", {
      method: "POST",
      body: JSON.stringify(body),
      timeoutMs: 300_000,
    }),
};

// --- domain API methods ---

export const authApi = {
  login: (username: string, password: string) =>
    request<{ token: string; expires_in: number; user: { id: number; username: string; role: string } }>(
      "/auth/login",
      { method: "POST", body: JSON.stringify({ username, password }) }
    ),
  loginSupplier: (username: string, password: string) =>
    request<{ token: string; expires_in: number; user: { id: number; username: string; role: string } }>(
      "/auth/login/supplier",
      { method: "POST", body: JSON.stringify({ username, password }) }
    ),
};

export const relaySitesApi = {
  list: () => request<{ items: RelaySite[]; total: number }>("/relay-sites"),
  get: (id: number) => request<RelaySite>(`/relay-sites/${id}`),
  create: (body: Partial<RelaySite> & { auth: string }) =>
    request<RelaySite>("/relay-sites", { method: "POST", body: JSON.stringify(body) }),
  update: (id: number, body: Partial<RelaySite> & { auth?: string }) =>
    request<RelaySite>(`/relay-sites/${id}`, { method: "PUT", body: JSON.stringify(body) }),
  remove: (id: number) => request<{ deleted: number }>(`/relay-sites/${id}`, { method: "DELETE" }),
  test: (id: number) =>
    request<{ ok: boolean; tested_at: string; error?: string }>(`/relay-sites/${id}/test`, { method: "POST" }),
  // doc/008: 保存前「先测后存」纯探针 —— 用表单里的新凭证直接探针,不落库、不改缓存。
  testCredentials: (body: {
    adapter_type: RelayAdapterType;
    url: string;
    auth_type: AuthType;
    auth: string;
    // 探针必须带业务模块路径：否则测的地址与实际分发地址不是同一个。
    site_options?: Record<string, string>;
    platform_user_id: number;
  }) =>
    request<{ ok: boolean; tested_at: string; error?: string }>("/relay-sites/test-credentials", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  proxy: (id: number, body: { method: string; path: string; query?: Record<string, string>; body?: unknown }) =>
    request<ConsoleProxyResult>(`/relay-sites/${id}/api`, { method: "POST", body: JSON.stringify(body) }),
};

export const pushSitesApi = {
  list: () => request<{ items: PushSite[]; total: number }>("/push-sites"),
  get: (id: number) => request<PushSite>(`/push-sites/${id}`),
  create: (body: Partial<PushSite> & { auth: string; user_name?: string }) =>
    request<PushSite>("/push-sites", { method: "POST", body: JSON.stringify(body) }),
  update: (id: number, body: Partial<PushSite> & { auth?: string; user_name?: string }) =>
    request<PushSite>(`/push-sites/${id}`, { method: "PUT", body: JSON.stringify(body) }),
  remove: (id: number) => request<{ deleted: number }>(`/push-sites/${id}`, { method: "DELETE" }),
  // doc/009:保存前「先测后存」纯探针 —— 用表单里的新凭证直接探商家。
  // 不落库、不改 last_tested_at;url 仅为元数据(商家 API 固定 BaseURL)。
  // basic 时 user_name 与 auth 一同提交,token 时 user_name 不传。
  testCredentials: (body: {
    adapter_type: string;
    url: string;
    auth_type: AuthType;
    auth: string;
    user_name?: string;
    field_mapping?: string;
    site_options?: Record<string, string>;
  }) =>
    request<{ ok: boolean; tested_at: string; error?: string }>("/push-sites/test-credentials", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  // v6: 推送站接口测试面板。templates 探测(连通性 + 模板列表);createTest 真实创建渠道。
  templates: (id: number) =>
    request<{ ok: boolean; tested_at: string; error?: string; templates?: PushSiteTemplate[] }>(
      `/push-sites/${id}/templates`,
      { method: "POST" }
    ),
  createTest: (id: number, body: PushSiteCreateTestReq) =>
    request<{ ok: boolean; outcome: string; remote_channel_id?: number; message?: string; error?: string }>(
      `/push-sites/${id}/create-test`,
      { method: "POST", body: JSON.stringify(body) }
    ),
  // v6: 通用接口代理。method/path 命中后端端点白名单,body 原样透传上游。
  // 返回归一结果 {code,message,data,upstream_status}。
  proxy: (
    id: number,
    body: { method: string; path: string; query?: Record<string, string>; body?: unknown }
  ) =>
    request<ConsoleProxyResult>(`/push-sites/${id}/api`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  channelLedger: (id: number, channelId: number) =>
    request<PushChannelLedger>(`/push-sites/${id}/channel-ledger?channel_id=${channelId}`),
};

export interface PushChannelLedger {
  channel_id: number;
  settlement_mode: string;
  currency: string;
  total_count: number;
  quota_total: number;
  usage_amount_total: number;
  usage_unsampled_count: number;
}

// v6: push_site /templates 探针返回的上游模板元数据(无密钥)。
export interface PushSiteTemplateInput {
  key: string;
  label: string;
  type: string;
  required: boolean;
}
export interface PushSiteTemplate {
  id: number;
  key: string;
  name: string;
  inputs: PushSiteTemplateInput[];
}

// v6: push_site /create-test 请求体。upstream_key 明文仅在测试面板瞬时使用,不落库。
export interface PushSiteCreateTestReq {
  upstream_key: string;
  template_id: number;
  rows: Record<string, string>[];
  site_ids?: number[];
  tag_id?: number;
  models?: string;
  model_mapping?: string;
}

export const keysApi = {
  list: (params?: {
    ids?: number[];
    supplier_field?: string;
    status?: KeyStatus;
    q?: string;
    created_after?: string;
    page?: number;
    page_size?: number;
    include_supplier_fields?: boolean;
    owner_supplier_id?: number;
    template_id?: number;
    site_id?: number;
    site_type?: "relay" | "push";
    include_task_counts?: boolean;
  }) => {
    const q = new URLSearchParams();
    if (params?.ids?.length) q.set("id", params.ids.join(","));
    if (params?.supplier_field) q.set("supplier_field", params.supplier_field);
    if (params?.status) q.set("status", params.status);
    if (params?.q) q.set("q", params.q);
    if (params?.created_after) q.set("created_after", params.created_after);
    if (params?.page) q.set("page", String(params.page));
    if (params?.page_size) q.set("page_size", String(params.page_size));
    if (params?.include_supplier_fields) q.set("include_supplier_fields", "true");
    if (params?.owner_supplier_id) q.set("owner_supplier_id", String(params.owner_supplier_id));
    if (params?.template_id) q.set("template_id", String(params.template_id));
    if (params?.site_id) q.set("site_id", String(params.site_id));
    if (params?.site_type) q.set("site_type", params.site_type);
    if (params?.include_task_counts) q.set("include_task_counts", "true");
    const qs = q.toString();
    return request<{
      items: APIKey[];
      total: number;
      page: number;
      page_size: number;
      supplier_fields: string[];
    }>(`/api-keys${qs ? `?${qs}` : ""}`);
  },
  get: (id: number) => request<APIKey>(`/api-keys/${id}`),
  create: (body: Partial<APIKey> & { key: string }) =>
    request<APIKey>("/api-keys", { method: "POST", body: JSON.stringify(body) }),
  batchCreate: (keys: Array<Partial<APIKey> & { key: string }>) =>
    request<{ created: number; failed: number; items: APIKey[]; errors: { index: number; name: string; error: string }[] }>(
      "/api-keys/batch",
      { method: "POST", body: JSON.stringify({ keys }), timeoutMs: 300_000 }
    ),
  update: (id: number, body: Partial<APIKey> & { key?: string }) =>
    request<APIKey>(`/api-keys/${id}`, { method: "PUT", body: JSON.stringify(body) }),
  remove: (id: number) => request<{ deleted: number; soft?: boolean }>(`/api-keys/${id}`, { method: "DELETE" }),
};

// suppliersApi — /protocol-types 协议类型 select 数据源(前 /providers,再前挂 /suppliers);
// 后端 provider_mapping 常量驱动。doc/049 Divergence:/suppliers 已让位给运营端
// 供应商账号管理(P5),只读列表迁移到 /protocol-types。
export const suppliersApi = {
  list: () => request<{ items: ProtocolType[]; total: number }>("/protocol-types"),
};

// 系统设置 → 协议类型:主协议(原厂)。v6→main 域重构:protocol_adapter_mapping
// 已删,protocol_type 直接落在 main_protocol;id 自增主键,code 唯一。
export interface MainProtocol {
  id: number;
  code: string;
  protocol_type: number;
  name: string;
  base_url: string;
  sort: number;
  enabled: boolean;
  remark?: string;
}
export const protocolsApi = {
  list: () => request<{ items: MainProtocol[]; total: number }>("/protocols"),
  create: (body: { code: string; name: string; protocol_type?: number; base_url?: string; sort?: number; enabled?: boolean; remark?: string }) =>
    request<MainProtocol>("/protocols", { method: "POST", body: JSON.stringify(body) }),
  update: (code: string, body: Partial<Pick<MainProtocol, "name" | "protocol_type" | "base_url" | "sort" | "enabled" | "remark">>) =>
    request<MainProtocol>(`/protocols/${code}`, { method: "PUT", body: JSON.stringify(body) }),
  remove: (code: string) => request<{ deleted: string }>(`/protocols/${code}`, { method: "DELETE" }),
};

export const distributionsApi = {
  list: (params?: {
    ids?: number[];
    key_id?: number;
    key_ids?: number[];
    operation_id?: string;
    site_id?: number;
    template_id?: number;
    site_type?: "relay" | "push";
    status?: TaskStatus | TaskStatus[];
    q?: string;
    created_after?: string;
    page?: number;
    page_size?: number;
    sort?: string;
    order?: SortOrder;
    latest_per_key?: boolean;
    latest_operation?: boolean;
    signal?: AbortSignal;
  }) => {
    const q = new URLSearchParams();
    if (params?.ids?.length) q.set("id", params.ids.join(","));
    if (params?.key_id) q.set("key_id", String(params.key_id));
    if (params?.key_ids?.length) q.set("key_ids", params.key_ids.join(","));
    if (params?.operation_id) q.set("operation_id", params.operation_id);
    if (params?.site_id) q.set("site_id", String(params.site_id));
    if (params?.template_id) q.set("template_id", String(params.template_id));
    if (params?.site_type) q.set("site_type", params.site_type);
    if (params?.status) q.set("status", Array.isArray(params.status) ? params.status.join(",") : params.status);
    if (params?.q) q.set("q", params.q);
    if (params?.created_after) q.set("created_after", params.created_after);
    if (params?.page) q.set("page", String(params.page));
    if (params?.page_size) q.set("page_size", String(params.page_size));
    if (params?.sort) q.set("sort", params.sort);
    if (params?.order) q.set("order", params.order);
    if (params?.latest_per_key) q.set("latest_per_key", "true");
    if (params?.latest_operation) q.set("latest_operation", "true");
    const qs = q.toString();
    return request<ListPageResult<DistributionTask>>(
      `/distributions${qs ? `?${qs}` : ""}`,
      { signal: params?.signal },
    );
  },
  // 混合批(005 分发合并):按 site_type 分组携带表内唯一 code。后端把 code 解析回
  // (site_type, site_id) 落库,一次提交可同时覆盖中转站 + 推送站。relay/push 至少一侧
  // 非空;priority 只对 relay 有意义(留空后端用默认),model_mapping 双侧都写入快照。
  // template_id 用于让 push 任务去 template_push_site 拿 push_template_id +
  // field_mapping(适配器 ezlink 走 /channels/batch 必须有);主模板触发的分发必传。
  create: (
    key_id: number,
    targets: { relaySiteCodes?: string[]; pushSiteCodes?: string[]; templateId?: number },
    opts?: { priority?: number; models?: string | null; model_mapping?: string | null; operation_id?: string; staged?: boolean; signal?: AbortSignal; timeoutMs?: number },
  ) =>
    request<{ tasks: DistributionTask[] }>("/distributions", {
      method: "POST",
      body: JSON.stringify({
        key_id,
        ...(targets.relaySiteCodes?.length ? { relay_site_codes: targets.relaySiteCodes } : {}),
        ...(targets.pushSiteCodes?.length ? { push_site_codes: targets.pushSiteCodes } : {}),
        ...(targets.templateId != null ? { template_id: targets.templateId } : {}),
        ...(opts?.priority != null ? { priority: opts.priority } : {}),
        ...(opts && "models" in opts ? { models: opts.models } : {}),
        ...(opts && "model_mapping" in opts ? { model_mapping: opts.model_mapping } : {}),
        ...(opts?.operation_id ? { operation_id: opts.operation_id } : {}),
        ...(opts?.staged ? { staged: true } : {}),
      }),
      signal: opts?.signal,
      timeoutMs: opts?.timeoutMs ?? 15_000,
    }),
  // get 返回任务详情 + 出站 HTTP 调用留痕(exchanges)。列表接口不携带该字段。
  get: (id: number) =>
    request<DistributionTaskDetail>(`/distributions/${id}`),
  retry: (id: number) =>
    request<DistributionTask>(`/distributions/${id}/retry`, { method: "POST" }),
  abandon: (id: number) =>
    request<DistributionTask>(`/distributions/${id}/abandon`, { method: "POST" }),
  sync: (id: number) =>
    request<DistributionTask>(`/distributions/${id}/sync`, { method: "POST" }),
  commit: (operation_id: string) =>
    request<{ operation_id: string; promoted: number }>("/distributions/commit", {
      method: "POST",
      body: JSON.stringify({ operation_id }),
    }),
};

export const usageApi = {
  listChannelSummaries: (keyIds?: number[]) => {
    const q = new URLSearchParams();
    if (keyIds?.length) q.set("key_id", keyIds.join(","));
    const qs = q.toString();
    return request<{ items: ChannelUsageSummary[]; total: number }>(
      `/usage/channel-summaries${qs ? `?${qs}` : ""}`
    );
  },
  listSnapshots: (params?: {
    task_id?: number;
    site_id?: number;
    key_id?: number;
    from?: string;
    to?: string;
    page?: number;
    page_size?: number;
    sort?: string;
    order?: SortOrder;
  }) => {
    const q = new URLSearchParams();
    if (params?.task_id) q.set("task_id", String(params.task_id));
    if (params?.site_id) q.set("site_id", String(params.site_id));
    if (params?.key_id) q.set("key_id", String(params.key_id));
    if (params?.from) q.set("from", params.from);
    if (params?.to) q.set("to", params.to);
    if (params?.page) q.set("page", String(params.page));
    if (params?.page_size) q.set("page_size", String(params.page_size));
    if (params?.sort) q.set("sort", params.sort);
    if (params?.order) q.set("order", params.order);
    const qs = q.toString();
    return request<ListPageResult<UsageSnapshot>>(`/usage/snapshots${qs ? `?${qs}` : ""}`);
  },
  listFetchLogs: (params?: {
    site_id?: number;
    result?: string;
    page?: number;
    page_size?: number;
    sort?: string;
    order?: SortOrder;
  }) => {
    const q = new URLSearchParams();
    if (params?.site_id) q.set("site_id", String(params.site_id));
    if (params?.result) q.set("result", params.result);
    if (params?.page) q.set("page", String(params.page));
    if (params?.page_size) q.set("page_size", String(params.page_size));
    if (params?.sort) q.set("sort", params.sort);
    if (params?.order) q.set("order", params.order);
    const qs = q.toString();
    return request<ListPageResult<UsageFetchLog>>(`/usage/fetch-logs${qs ? `?${qs}` : ""}`);
  },
  refresh: (keyIds: number[]) =>
    request<UsageRefreshResult>("/usage/refresh", {
      method: "POST",
      body: JSON.stringify({ key_ids: keyIds }),
      // A bounded 12-key refresh currently takes about 200 seconds against
      // the remote PG and seven direct sites. Keep this operation's timeout
      // separate from ordinary API calls so its successful response can
      // trigger the summary refetch instead of leaving stale detail on screen.
      timeoutMs: 600_000,
    }),
};

// 模板 API — /main-templates(v6→main 域重构:id 主键,引用 main_protocol;删
// adapter_type/groups/priority)。中转站绑定 = template_relay_site(groups+priority),
// 推送站绑定 = template_push_site(push_template_id+field_mapping)。

// mainTemplatesApi — 主模板 CRUD(按 id)。
export const mainTemplatesApi = {
  list: (params?: { enabled?: boolean }) => {
    const qs = params?.enabled != null ? `?enabled=${params.enabled}` : "";
    return request<{ items: MainTemplate[]; total: number }>(`/main-templates${qs}`);
  },
  get: (id: number) => request<MainTemplate>(`/main-templates/${id}`),
  create: (body: {
    main_protocol_id: number;
    code: string;
    name: string;
    eyebrow?: string;
    description?: string;
    models?: string;
    model_mapping?: string;
    enabled?: boolean;
  }) => request<MainTemplate>("/main-templates", { method: "POST", body: JSON.stringify(body) }),
  update: (id: number, body: Partial<Pick<MainTemplate, "main_protocol_id" | "name" | "eyebrow" | "description" | "models" | "model_mapping" | "enabled">>) =>
    request<{ updated: number }>(`/main-templates/${id}`, { method: "PUT", body: JSON.stringify(body) }),
  remove: (id: number) =>
    request<{ deleted: number }>(`/main-templates/${id}`, { method: "DELETE" }),
};

// templateRelaySitesApi — 中转站绑定。汇总走 GET /template-relay-sites;
// 增删改嵌套在 /main-templates/:id/sites。
export const templateRelaySitesApi = {
  listAll: () => request<{ items: TemplateRelaySite[]; total: number }>("/template-relay-sites"),
  bind: (templateId: number, body: { site_id: number; groups?: string; priority?: number; proxy?: string; models?: string | null; model_mapping?: string | null }) =>
    request<TemplateRelaySite>(`/main-templates/${templateId}/sites`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  update: (templateId: number, siteId: number, body: { groups?: string; priority?: number; proxy?: string; models?: string | null; model_mapping?: string | null }) =>
    request<{ updated: boolean }>(`/main-templates/${templateId}/sites/${siteId}`, {
      method: "PUT",
      body: JSON.stringify(body),
    }),
  unbind: (templateId: number, siteId: number) =>
    request<{ deleted: boolean }>(`/main-templates/${templateId}/sites/${siteId}`, { method: "DELETE" }),
};

// templatePushSitesApi — 推送站绑定。汇总走 GET /template-push-sites;
// 增删改嵌套在 /main-templates/:id/push-sites。
export const templatePushSitesApi = {
  listAll: () => request<{ items: TemplatePushSite[]; total: number }>("/template-push-sites"),
  bind: (templateId: number, body: { site_id: number; push_template_id?: number; field_mapping?: string; models?: string | null; model_mapping?: string | null }) =>
    request<TemplatePushSite>(`/main-templates/${templateId}/push-sites`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  update: (templateId: number, siteId: number, body: { push_template_id?: number; field_mapping?: string; models?: string | null; model_mapping?: string | null }) =>
    request<{ updated: boolean }>(`/main-templates/${templateId}/push-sites/${siteId}`, {
      method: "PUT",
      body: JSON.stringify(body),
    }),
  unbind: (templateId: number, siteId: number) =>
    request<{ deleted: boolean }>(`/main-templates/${templateId}/push-sites/${siteId}`, { method: "DELETE" }),
};

// UsageSnapshot / UsageFetchLog — 用量采集(不变)。
export interface UsageSnapshot {
  id: number;
  distribution_task_id: number;
  sampled_at: string;
  used_quota: number;
  source: "scheduled" | "manual";
}

export interface UsageFetchLog {
  id: number;
  site_id: number;
  site_type: "relay" | "push";
  sampled_from: string;
  sampled_to: string;
  result: "success" | "failed" | "partial";
  error: string;
  snapshot_count: number;
  source: string;
  occurred_at: string;
}

export interface UsageRefreshResult {
  key_ids: number[];
  key_count: number;
  task_count: number;
  source_count: number;
  success_count: number;
  partial_count: number;
  failed_count: number;
  completed_at: string;
}

export interface ChannelUsageSite {
  downstream_ref: string;
  downstream_name?: string;
  value: number | null;
  sampled_at: string | null;
  last_attempt_at: string;
  status: UsageAttemptStatus;
  error?: string;
}

export interface ChannelUsageSource {
  source_site_type: "relay" | "push";
  source_site_id: number;
  source_site_name: string;
  target_code?: string;
  total: number | null;
  coverage: UsageCoverage;
  status: UsageAttemptStatus;
  error?: string;
  granularity: UsageGranularity;
  metric_key?: string;
  metric_version?: number;
  display_unit?: "USD";
  reported_total: number | null;
  reported_target_count: number | null;
  last_attempt_at: string | null;
  last_success_at: string | null;
  sites: ChannelUsageSite[];
}

export interface ChannelUsageSummary {
  key_id: number;
  total: number | null;
  display_unit: "USD";
  coverage: UsageCoverage;
  source_site_count: number;
  valued_source_count: number;
  downstream_site_count: number;
  valued_site_count: number;
  latest_sampled_at: string | null;
  sources: ChannelUsageSource[];
}
